import { AGREEMENT_PERMISSIONS } from './agreementPermissions'
import { BILL_PERMISSIONS } from './billPermissions'
import { FGA_VERBS_BY_RELATION } from './generated/model'
import { REQUEST_ORGANISATION_PERMISSIONS, REQUEST_PERMISSIONS } from './requestPermissions'
import { SUPPLIER_ORGANISATION_PERMISSIONS, SUPPLIER_PERMISSIONS } from './supplierPermissions'
import { WORKFLOW_PERMISSIONS } from './workflowPermissions'
import type { CoreDataDimension } from './coreDataDimensions'
import type { FgaAssignableRelation } from './generated/model'

/**
 * The entities a permission set can govern, and the entities the per-record access API serves. A
 * set's entity is fixed at creation.
 */
export const PERMISSION_SET_ENTITIES = ['request', 'supplier', 'agreement', 'bill', 'workflow'] as const
export type PermissionSetEntity = (typeof PERMISSION_SET_ENTITIES)[number]

/** The permission vocabulary of each entity, keyed by entity. */
export const PERMISSIONS_BY_ENTITY = {
  request: REQUEST_PERMISSIONS,
  supplier: SUPPLIER_PERMISSIONS,
  agreement: AGREEMENT_PERMISSIONS,
  bill: BILL_PERMISSIONS,
  workflow: WORKFLOW_PERMISSIONS,
} as const satisfies Record<PermissionSetEntity, readonly string[]>

/**
 * A dimension may only be listed for an entity whose `derived_scope` can hold that dimension's
 * values — a grant on a dimension the entity carries no edge for writes a well-formed tuple that
 * resolves to nothing. A bill or workflow has no scope edge at all, so it accepts none.
 */
export const SCOPE_DIMENSIONS_BY_ENTITY = {
  request: ['department', 'subsidiary'],
  supplier: ['department', 'subsidiary'],
  agreement: ['department', 'subsidiary'],
  bill: [],
  workflow: [],
} as const satisfies Record<PermissionSetEntity, readonly CoreDataDimension[]>

/**
 * How many changes the grant endpoints accept in one batch. Each change costs several sequential
 * OpenFGA round-trips to converge, so an unbounded batch would run into the lambda timeout mid-write
 * — a partial save — rather than being rejected cleanly.
 */
export const MAX_GRANT_CHANGES_PER_BATCH = 50

/**
 * The most records one batched permission read answers for, whatever the entity: the size it was load-tested
 * at. A longer list page splits into reads of this size and sends them in parallel, which load-tested as fast
 * as one read, so a larger cap would save requests rather than time.
 */
export const MAX_RECORDS_PER_PERMISSION_READ = 40

/**
 * The relations naming who takes part in a record, per entity — the model's `explicit_view` minus
 * the grant and supplier read-through arms, which are not held on the record itself.
 *
 * An agreement lists owners only: the supplier's contacts read through to it, but that access is
 * held on the supplier, so an access API cannot revoke it there.
 *
 * Named rather than derived, so a relation the model gains is a decision about whether it confers
 * revocable access. `modelWiring.test.ts` fails until that decision is made.
 */
export const PARTICIPANT_RELATIONS_BY_ENTITY = {
  request: ['derived_requester', 'derived_collaborator', 'derived_rfx_owner'],
  supplier: ['derived_contact'],
  agreement: ['derived_owner'],
  bill: ['derived_approver', 'derived_draft_assignee', 'derived_mention'],
  workflow: [],
} as const satisfies { [Entity in PermissionSetEntity]: readonly FgaAssignableRelation<Entity>[] }

/** Any relation naming a participant, across every entity. */
export type ParticipantRelation = (typeof PARTICIPANT_RELATIONS_BY_ENTITY)[PermissionSetEntity][number]

/**
 * A permission of the named entity. Callers holding a runtime entity get the union; use the guard to
 * narrow.
 */
export type PermissionOf<Entity extends PermissionSetEntity> = (typeof PERMISSIONS_BY_ENTITY)[Entity][number]

export type PermissionSetPermission = PermissionOf<PermissionSetEntity>

/**
 * Whether the permission belongs to the entity's vocabulary. The entities' permissions overlap in
 * shape but not in meaning, and one sent under the wrong entity is rejected by the permissions API, so
 * anything crossing that boundary has to be checked rather than assumed.
 */
export const isPermissionOfEntity = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permission: string
): permission is PermissionOf<Entity> => {
  const vocabulary: readonly string[] = PERMISSIONS_BY_ENTITY[entity]
  return vocabulary.includes(permission)
}

/** The entity's permission where its vocabulary has exactly one, so a grant on it has no level to choose. */
export const onlyPermissionOf = (entity: PermissionSetEntity): PermissionSetPermission | null => {
  const vocabulary: readonly PermissionSetPermission[] = PERMISSIONS_BY_ENTITY[entity]
  const [firstPermission, ...otherPermissions] = vocabulary
  return firstPermission != null && otherPermissions.length === 0 ? firstPermission : null
}

/** The entity's permissions, in vocabulary order, filtered to those present in `permissions`. */
export const permissionsOfEntity = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permissions: readonly string[]
): PermissionOf<Entity>[] => {
  const vocabulary: readonly PermissionOf<Entity>[] = PERMISSIONS_BY_ENTITY[entity]
  return vocabulary.filter((permission) => permissions.includes(permission))
}

const ORGANISATION_PERMISSIONS_BY_ENTITY = {
  request: REQUEST_ORGANISATION_PERMISSIONS,
  supplier: SUPPLIER_ORGANISATION_PERMISSIONS,
  agreement: [],
  bill: [],
  workflow: [],
} as const satisfies { [Entity in PermissionSetEntity]: readonly PermissionOf<Entity>[] }

/** The permissions a set holding any other record permission must also hold, per entity. */
export const MINIMUM_PERMISSIONS_BY_ENTITY = {
  request: ['view'],
  supplier: ['view'],
  agreement: ['view'],
  bill: [],
  workflow: [],
} as const satisfies { [Entity in PermissionSetEntity]: readonly PermissionOf<Entity>[] }

/**
 * The permissions a set must also hold because it holds `permissions`. Every permission but the
 * minimum itself and the organisation permissions confers the minimum in the model, so it is held
 * whether or not it is stored.
 */
export const requiredMinimumPermissions = <Entity extends PermissionSetEntity>(
  entity: Entity,
  permissions: readonly PermissionSetPermission[]
): readonly PermissionOf<Entity>[] => {
  const minimumPermissions: readonly PermissionOf<Entity>[] = MINIMUM_PERMISSIONS_BY_ENTITY[entity]
  const exemptPermissions: readonly PermissionSetPermission[] = [
    ...minimumPermissions,
    ...ORGANISATION_PERMISSIONS_BY_ENTITY[entity],
  ]
  return permissions.some((permission) => !exemptPermissions.includes(permission)) ? minimumPermissions : []
}

/**
 * The entities the record-permission reads answer for: every permission-set entity, plus documents, which no
 * permission set governs but whose rows a client still asks about.
 */
export const RECORD_PERMISSIONS_ENTITIES = [...PERMISSION_SET_ENTITIES, 'document'] as const
export type RecordPermissionsEntity = (typeof RECORD_PERMISSIONS_ENTITIES)[number]

/** The permissions the model answers per record, which need not match what a permission set toggles. */
export const RECORD_PERMISSIONS_BY_ENTITY = {
  request: [
    'view',
    'edit_metadata',
    'edit_po_number',
    'manage_participants',
    'manage_followers',
    'pause',
    'reject',
    'delete',
    'edit_submissions',
    'view_documents',
    'edit_documents',
    'manage_documents',
    'manage_assignees',
    'skip',
    'rollback',
    'manage_integrations',
    'manage_sourcing',
    'administer',
    'view_tprm',
    'manage_tprm',
  ],
  supplier: [
    'view',
    'edit',
    'view_tprm',
    'manage_tprm',
    'view_related_requests',
    'view_related_agreements',
    'view_related_documents',
    'view_related_submissions',
    'edit_documents',
    'manage_documents',
    'manage_internal_contacts',
    'manage_external_contacts',
    'manage_products',
    'create_product',
    'add_agreement',
    'administer',
  ],
  agreement: [
    'view',
    'edit',
    'cancel',
    'delete',
    'view_related_supplier',
    'view_related_requests',
    'manage_renewals',
    'view_documents',
    'edit_documents',
    'manage_documents',
    'administer',
  ],
  bill: ['view', 'edit_before_accept', 'accept', 'assign', 'approve'],
  workflow: ['create_request'],
  document: ['view', 'edit', 'manage'],
} as const satisfies Record<RecordPermissionsEntity, readonly string[]>

export type RecordPermissionOf<Entity extends RecordPermissionsEntity> = (typeof RECORD_PERMISSIONS_BY_ENTITY)[Entity][number]

export type RecordPermission = RecordPermissionOf<RecordPermissionsEntity>

/**
 * What holding the relation lets a subject do on the record, read from the model rather than
 * restated. Scoped to what the model answers per record, so `can_create` — answered per
 * organisation — is dropped.
 */
export const permissionsForRelations = <Entity extends RecordPermissionsEntity>(
  entity: Entity,
  relations: readonly string[]
): RecordPermissionOf<Entity>[] => {
  const verbsByRelation: Record<string, readonly string[]> = FGA_VERBS_BY_RELATION[entity]
  const conferred = new Set(
    relations.flatMap((relation) => verbsByRelation[relation] ?? []).map((verb) => verb.replace(/^can_/, ''))
  )
  const vocabulary: readonly RecordPermissionOf<Entity>[] = RECORD_PERMISSIONS_BY_ENTITY[entity]
  return vocabulary.filter((permission) => conferred.has(permission))
}
